"use client";
// Shared finance/pari design-language helpers for Lane 6 (chat6). Composed from
// --ck tokens; pair these with @/design primitives (Button, Card, Input...) when
// migrating a finance/pari route. Self-contained, no data deps.
//   FinancePage , backlink + eyebrow + title + lead + content width
//   SectionHeader, Tint, Tile, StatGrid, RecoLink, SplitBar
import type { ReactNode } from "react";
import Link from "next/link";
import { ArrowLeft, ChevronRight } from "lucide-react";
import styles from "./Finance.module.css";

const cx = (...p: Array<string | false | undefined>) => p.filter(Boolean).join(" ");

export function FinancePage({ backHref, backLabel, eyebrow, title, lead, wide, children }: {
  backHref?: string; backLabel?: ReactNode; eyebrow?: ReactNode; title: ReactNode; lead?: ReactNode; wide?: boolean; children: ReactNode;
}) {
  return (
    <div className={wide ? styles.pageWide : styles.page} data-ck="v2">
      {backHref != null && (
        <Link className={styles.backlink} href={backHref}><ArrowLeft size={14} /> {backLabel}</Link>
      )}
      {eyebrow != null && <p className={styles.eyebrow}>{eyebrow}</p>}
      <h1 className={styles.title}>{title}</h1>
      {lead != null && <p className={styles.lead}>{lead}</p>}
      {children}
    </div>
  );
}

export function SectionHeader({ title, hint }: { title: ReactNode; hint?: ReactNode }) {
  return (
    <div className={styles.sectionH}>
      <h3>{title}</h3>
      {hint != null && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}

export type TintTone = "stone" | "green" | "red" | "warn" | "brand";
const tintClass: Record<TintTone, string> = { stone: styles.tintStone, green: styles.tintGreen, red: styles.tintRed, warn: styles.tintWarn, brand: styles.tintBrand };

export function Tint({ tone = "stone", className, children }: { tone?: TintTone; className?: string; children: ReactNode }) {
  return <div className={cx(styles.tint, tintClass[tone], className)}>{children}</div>;
}

export type TileTone = "default" | "pos" | "brand" | "neg" | "warn";
const tileVClass: Record<TileTone, string | false> = { default: false, pos: styles.tileVPos, brand: styles.tileVBrand, neg: styles.tileVNeg, warn: styles.tileVWarn };

export function Tile({ label, value, sub, tone = "default" }: { label: ReactNode; value: ReactNode; sub?: ReactNode; tone?: TileTone }) {
  return (
    <div className={styles.tile}>
      <p className={styles.tileK}>{label}</p>
      <p className={cx(styles.tileV, tileVClass[tone])}>{value}</p>
      {sub != null && <p className={styles.tileS}>{sub}</p>}
    </div>
  );
}

export function StatGrid({ cols = 4, children, style }: { cols?: number; children: ReactNode; style?: React.CSSProperties }) {
  return <div className={styles.statGrid} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, ...style }}>{children}</div>;
}

export type RecoTone = "default" | "danger";
const recoIcoTone: Record<string, string> = { default: styles.recoIco, green: cx(styles.recoIco, styles.recoIcoGreen), red: cx(styles.recoIco, styles.recoIcoRed), warn: cx(styles.recoIco, styles.recoIcoWarn) };

export function RecoLink({ href, onClick, icon, iconTone = "default", title, desc, tone = "default" }: {
  href?: string; onClick?: () => void; icon?: ReactNode; iconTone?: "default" | "green" | "red" | "warn"; title: ReactNode; desc?: ReactNode; tone?: RecoTone;
}) {
  const cls = cx(styles.recoLink, tone === "danger" && styles.recoDanger);
  const inner = (
    <>
      {icon != null && <span className={recoIcoTone[iconTone]}>{icon}</span>}
      <span className={styles.recoBody}>
        <span className={styles.recoTitle}>{title}</span>
        {desc != null && <span className={styles.recoDesc}>{desc}</span>}
      </span>
      <ChevronRight size={16} className={styles.recoChevron} />
    </>
  );
  if (href != null) return <Link className={cls} href={href}>{inner}</Link>;
  return <button type="button" className={cls} onClick={onClick}>{inner}</button>;
}

export function SplitBar({ segments }: { segments: Array<{ pct: number; color: string }> }) {
  return (
    <div className={styles.split}>
      {segments.filter(s => s.pct > 0).map((s, i) => (
        <span key={i} style={{ background: s.color, width: `${s.pct}%` }} />
      ))}
    </div>
  );
}

export function Legend({ items }: { items: Array<{ color: string; label: ReactNode }> }) {
  return (
    <div className={styles.legend}>
      {items.map((it, i) => (<span key={i}><i style={{ background: it.color }} />{it.label}</span>))}
    </div>
  );
}
